import { useId, useState } from 'react';
import {
  useVocabularies,
  useCreateVocabulary,
  useReplaceVocabularyTerms,
  usePublishVocabulary,
  useUsersByIds,
  useMe,
  useDeleteVocabulary,
} from '../lib/queries';
import { Badge, Modal, formatDate } from '../components/ui';
import {
  LANGUAGES,
  DEFAULT_LANGUAGE,
  languageLabel,
  vocabularyIsUsable,
  vocabularyUnusableReason,
} from '../lib/languages';
import type { VocabTerm, Vocabulary } from '@scribe/core/types';

export function VocabularyScreen() {
  const { data: vocabularies, isLoading, error, refetch } = useVocabularies();
  // Resolve the creators in one batched request — vocabularies are tenant-wide, so most rows in
  // this list belong to somebody else.
  const creators = useUsersByIds((vocabularies?.items ?? []).map((v) => v.owner_id));
  // ADMIN-5 uses this same hook for the nav gate — do not add a second `useMe`.
  const isAdmin = useMe().data?.is_admin === true;
  const deleteVocab = useDeleteVocabulary();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const selected = vocabularies?.items.find((v) => v.id === selectedId) ?? null;

  return (
    <div className="card">
      <div className="card-head">
        <h1>Custom vocabulary</h1>
        <button onClick={() => setCreating(true)}>New vocabulary</button>
      </div>
      <p className="muted">
        Draft terms, then publish an immutable version to AWS Transcribe. Vocabularies are shared
        with everyone at Umbrage — anyone can edit them, and only an admin can delete one. Open one
        to add terms; the editor explains what goes in each column.
      </p>

      {error && (
        <div className="banner banner-danger">
          <p>{(error as Error).message || 'Failed to load vocabularies.'}</p>
          <button className="sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}
      {isLoading && <p className="muted">Loading vocabularies…</p>}

      {vocabularies && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Language</th>
                {/* Vocabularies are tenant-wide and anyone may edit them, so the list would
                    otherwise be anonymous — you could not tell whose terms you were about to
                    change, or who to ask about them. */}
                <th>Created by</th>
                <th>Published version</th>
                <th>Engine status</th>
                <th>Terms</th>
                <th>Updated</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vocabularies.items.length === 0 && (
                <tr>
                  <td className="empty" colSpan={8}>
                    No vocabularies yet — create one to bias transcription toward your team's terms. Vocabularies are shared with everyone at Umbrage.
                  </td>
                </tr>
              )}
              {vocabularies.items.map((v) => (
                <tr key={v.id}>
                  <td>{v.name}</td>
                  <td>{languageLabel(v.language)}</td>
                  <td className="muted">
                    {creators.byId.get(v.owner_id)?.display_name ?? '—'}
                  </td>
                  <td>
                    {v.published_version != null ? (
                      <Badge kind="success">v{v.published_version}</Badge>
                    ) : (
                      <Badge kind="neutral">unpublished</Badge>
                    )}
                  </td>
                  {/* §2.6 step 3: published is NOT the same as usable. A vocabulary AWS has not
                      finished registering will not bias anything, and the transcription picker
                      leaves it out — without this column that omission looks like a bug. */}
                  <td>
                    {vocabularyIsUsable(v) ? (
                      <Badge kind="success">Ready</Badge>
                    ) : (
                      <Badge kind="neutral">{vocabularyUnusableReason(v)}</Badge>
                    )}
                  </td>
                  <td>{v.terms.length}</td>
                  <td>{formatDate(v.updated_at)}</td>
                  <td className="right">
                    <button className="ghost sm" onClick={() => setSelectedId(v.id)}>
                      Edit
                    </button>
                    {/* Admin only, and gated on `is_admin` rather than on authorship: editing is
                        collaborative because a bad edit is undone by re-publishing, but deleting
                        is destructive for the whole tenant and the terms are simply gone. The
                        server enforces this too (403) — this only avoids offering a button that
                        would fail. */}
                    {isAdmin && (
                      <button
                        className="ghost sm danger"
                        disabled={deleteVocab.isPending}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Delete "${v.name}" for everyone at Umbrage? Its terms cannot be ` +
                                `recovered, and recordings that used it will need a different ` +
                                `vocabulary if they are transcribed again.`,
                            )
                          ) {
                            return;
                          }
                          deleteVocab.mutate(v.id);
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateVocabularyForm
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setSelectedId(id);
          }}
        />
      )}
      {selected && <EditVocabulary vocabulary={selected} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function CreateVocabularyForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const createVocab = useCreateVocabulary();
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<string>(DEFAULT_LANGUAGE);
  const titleId = useId();
  const languageId = useId();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createVocab.mutate(
      { name: name.trim(), language, terms: [] },
      {
        onSuccess: (v) => onCreated(v.id),
      },
    );
  }

  return (
    <Modal onClose={onClose} labelledBy={titleId}>
      <h2 id={titleId}>New vocabulary</h2>
      <form onSubmit={submit}>
        <div className="field">
          <span className="label">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ProductTerms"
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={languageId}>
            Language
          </label>
          {/* Was a free-text input, which let a typo ("en_US", "english") through to AWS as a
              LanguageCode and produced a vocabulary nothing could ever use. Reuses the same
              shared list the transcription controls offer, so a vocabulary can only be built
              for a language a recording can actually request. */}
          <select id={languageId} value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
        {createVocab.isError && <p className="error">{(createVocab.error as Error).message}</p>}
        <div className="row">
          <button disabled={!name.trim() || createVocab.isPending}>
            {createVocab.isPending ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** AWS Transcribe custom-vocabulary phrases must not contain digits — see the screen's own
 * help copy above the table. Numerals belong in `display_as` instead. */
const PHRASE_HAS_DIGITS = /\d/;

function EditVocabulary({ vocabulary, onClose }: { vocabulary: Vocabulary; onClose: () => void }) {
  const [terms, setTerms] = useState<VocabTerm[]>(vocabulary.terms);
  const [phrase, setPhrase] = useState('');
  const [displayAs, setDisplayAs] = useState('');
  const [soundsLike, setSoundsLike] = useState('');
  const replaceTerms = useReplaceVocabularyTerms();
  const publish = usePublishVocabulary();
  const titleId = useId();

  const phraseHasDigits = PHRASE_HAS_DIGITS.test(phrase);

  function addTerm() {
    if (!phrase.trim() || !displayAs.trim() || phraseHasDigits) return;
    setTerms((t) => [
      ...t,
      {
        phrase: phrase.trim(),
        display_as: displayAs.trim(),
        sounds_like: soundsLike.trim() || null,
      },
    ]);
    setPhrase('');
    setDisplayAs('');
    setSoundsLike('');
  }

  function removeTerm(i: number) {
    setTerms((t) => t.filter((_, idx) => idx !== i));
  }

  function save() {
    replaceTerms.mutate({ id: vocabulary.id, terms });
  }

  /* Publish ships whatever terms the SERVER has (the API takes only the vocabulary id and
   * reads `record.terms` — see api/routers/vocabularies.py), so gating the button on the
   * LOCAL `terms` let unsaved edits publish an EMPTY vocabulary while reporting success.
   * That shipped a header-only 32-byte terms file to S3 and created nothing in Transcribe,
   * silently (found in M10 dev validation T7, 2026-08-06). Gate on the persisted state and
   * block while there are unsaved edits. `vocabulary` refreshes after a save — the mutation
   * invalidates ['vocabularies'] and the parent re-derives this prop from the list. */
  const savedTerms = vocabulary.terms;
  const hasUnsavedEdits =
    terms.length !== savedTerms.length ||
    terms.some((t, i) => {
      const saved = savedTerms[i];
      return (
        !saved ||
        t.phrase !== saved.phrase ||
        t.display_as !== saved.display_as ||
        (t.sounds_like ?? null) !== (saved.sounds_like ?? null)
      );
    });
  const publishBlockedReason = hasUnsavedEdits
    ? 'Save draft before publishing — publishing ships the saved terms.'
    : savedTerms.length === 0
      ? 'Add and save at least one term before publishing.'
      : null;

  return (
    <Modal onClose={onClose} wide labelledBy={titleId}>
      <h2 id={titleId}>
        {vocabulary.name} <span className="muted">({languageLabel(vocabulary.language)})</span>
      </h2>

      <TermGuidance />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Phrase</th>
              <th>Sounds like</th>
              <th>Display as</th>
              <th className="right"></th>
            </tr>
          </thead>
          <tbody>
            {terms.length === 0 && (
              <tr>
                <td className="empty" colSpan={4}>
                  No terms yet.
                </td>
              </tr>
            )}
            {terms.map((t, i) => (
              <tr key={i}>
                <td>{t.phrase}</td>
                <td>{t.sounds_like ?? '—'}</td>
                <td>{t.display_as}</td>
                <td className="right">
                  <button className="ghost sm" onClick={() => removeTerm(i)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <input
          placeholder="Phrase (no digits)"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
        />
        <input
          placeholder="Sounds like (optional)"
          value={soundsLike}
          onChange={(e) => setSoundsLike(e.target.value)}
        />
        <input
          placeholder="Display as"
          value={displayAs}
          onChange={(e) => setDisplayAs(e.target.value)}
        />
        <button
          type="button"
          className="ghost"
          onClick={addTerm}
          disabled={!phrase.trim() || !displayAs.trim() || phraseHasDigits}
        >
          Add term
        </button>
      </div>
      {phraseHasDigits && (
        <p className="error">Phrase cannot contain digits — put numerals in "display as".</p>
      )}

      {publishBlockedReason && <p className="muted">{publishBlockedReason}</p>}
      {replaceTerms.isError && <p className="error">{(replaceTerms.error as Error).message}</p>}
      {publish.isError && <p className="error">{(publish.error as Error).message}</p>}
      {publish.isSuccess && (
        <p className="muted" style={{ color: 'var(--ink)' }}>
          Publish requested — engine state PENDING until AWS Transcribe finishes processing.
        </p>
      )}

      <div className="row row-secondary">
        <button onClick={save} disabled={replaceTerms.isPending}>
          {replaceTerms.isPending ? 'Saving…' : 'Save draft'}
        </button>
        <button
          onClick={() => publish.mutate(vocabulary.id)}
          disabled={publish.isPending || publishBlockedReason !== null}
          title={publishBlockedReason ?? undefined}
        >
          {publish.isPending ? 'Publishing…' : 'Publish'}
        </button>
        <button className="ghost" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

/**
 * §2.6 step 5. Collapsed by default: someone adding their tenth term does not need the
 * explanation, and someone adding their first cannot guess it. The three columns are not
 * self-explanatory — "Sounds like" in particular reads as optional flavour when it is the field
 * that actually fixes a mis-heard name — and the digit rule is a hard AWS constraint that a user
 * hits as a rejection rather than as advice.
 *
 * Written from what the code actually enforces (`domain/vocab.validate_phrase`) and what the
 * engine actually consumes, not from general advice.
 */
function TermGuidance() {
  return (
    <details className="guidance">
      <summary>How to write a good term</summary>
      <dl className="guidance-list">
        <dt>Phrase</dt>
        <dd>
          The words as they are <em>spoken</em> in the recording. Join words that are said
          together with hyphens — <code>Five-Nine</code>, not <code>Five Nine</code>.
          {' '}
          <strong>No digits.</strong> Transcribe rejects a vocabulary whose phrase contains
          one, so write <code>Five-Nine</code> here and put <code>Five9</code> in Display as.
        </dd>

        <dt>Sounds like</dt>
        <dd>
          Only needed when a phrase is not pronounced the way it is spelled — which is most
          product names and surnames. Spell it in ordinary syllables, hyphenated, as you would
          read it aloud: <code>um-bridge</code> for <em>Umbrage</em>. This is usually the field
          that fixes a name the transcript keeps getting wrong. Leave it blank when the spelling
          already matches the pronunciation.
        </dd>

        <dt>Display as</dt>
        <dd>
          How the term should appear in the finished transcript, with its real punctuation,
          capitalisation and digits: <code>Five9</code>, <code>C3.ai</code>,{' '}
          <code>Umbrage</code>. This is what a reader sees.
        </dd>
      </dl>

      <p className="muted small">
        A short, specific list beats a long one. Add the names, acronyms and jargon that are
        actually getting mis-heard — people, products, clients, internal terms. Ordinary English
        words do not need entries, and adding a lot of them can make recognition worse rather
        than better.
      </p>
    </details>
  );
}
